/**
 * File Import Component for Tonga Musika
 * Supports: MusicXML (.xml, .mxl), PDF, and Image uploads
 * Uses 100% open-source libraries
 */

import React, { useState, useRef } from 'react';
import { Upload, File, Image, Music, AlertCircle, CheckCircle, Loader } from 'lucide-react';

const FileImport = ({ onMusicDataParsed, onError }) => {
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileType, setFileType] = useState('');
  const [parseStatus, setParseStatus] = useState('');
  const fileInputRef = useRef(null);

  const supportedFormats = {
    musicxml: {
      extensions: ['.xml', '.mxl', '.musicxml'],
      mimeTypes: ['application/vnd.recordare.musicxml+xml', 'application/vnd.recordare.musicxml', 'text/xml'],
      icon: Music,
      label: 'MusicXML Files',
      parser: 'parseMusicXML'
    },
    pdf: {
      extensions: ['.pdf'],
      mimeTypes: ['application/pdf'],
      icon: File,
      label: 'PDF Sheet Music',
      parser: 'parsePDF'
    },
    image: {
      extensions: ['.png', '.jpg', '.jpeg'],
      mimeTypes: ['image/png', 'image/jpeg'],
      icon: Image,
      label: 'Scanned Sheet Music',
      parser: 'parseImage'
    }
  };

  /**
   * Helper function to extract measures with voice separation
   */
  const extractMeasuresWithVoices = (partElement) => {
    const measures = partElement.querySelectorAll('measure');
    const voice1Data = [];
    const voice2Data = [];
    let partClef = null; // Track the clef for this part
    
    measures.forEach(measure => {
      const v1Data = {
        number: measure.getAttribute('number'),
        notes: [],
        clef: partClef
      };
      const v2Data = {
        number: measure.getAttribute('number'),
        notes: [],
        clef: partClef
      };

      // Extract attributes (key, time, clef)
      const attributes = measure.querySelector('attributes');
      if (attributes) {
        const fifths = attributes.querySelector('key fifths')?.textContent;
        const timeBeats = attributes.querySelector('time beats')?.textContent;
        const timeBeatType = attributes.querySelector('time beat-type')?.textContent;
        
        // Get clef
        const clef = attributes.querySelector('clef');
        if (clef) {
          const sign = clef.querySelector('sign')?.textContent;
          const line = clef.querySelector('line')?.textContent;
          partClef = `${sign}${line}`; // e.g., "G2" (treble) or "F4" (bass)
          v1Data.clef = partClef;
          v2Data.clef = partClef;
        }
        
        if (fifths) {
          v1Data.key = fifths;
          v2Data.key = fifths;
        }
        if (timeBeats && timeBeatType) {
          v1Data.timeSignature = `${timeBeats}/${timeBeatType}`;
          v2Data.timeSignature = `${timeBeats}/${timeBeatType}`;
        }
      }

      // Extract notes and separate by voice
      const notes = measure.querySelectorAll('note');
      notes.forEach(note => {
        const voice = note.querySelector('voice')?.textContent || '1';
        const isRest = note.querySelector('rest') !== null;
        const noteData = { isRest };

        if (!isRest) {
          const pitch = note.querySelector('pitch');
          if (pitch) {
            noteData.step = pitch.querySelector('step')?.textContent;
            noteData.octave = pitch.querySelector('octave')?.textContent;
            noteData.alter = pitch.querySelector('alter')?.textContent;
          }
        }

        noteData.duration = note.querySelector('duration')?.textContent;
        noteData.type = note.querySelector('type')?.textContent;

        // Add to appropriate voice
        if (voice === '1') {
          v1Data.notes.push(noteData);
        } else if (voice === '2') {
          v2Data.notes.push(noteData);
        }
      });

      voice1Data.push(v1Data);
      if (v2Data.notes.length > 0) {
        voice2Data.push(v2Data);
      }
    });
    
    return { voice1: voice1Data, voice2: voice2Data };
  };

  /**
   * Parse MusicXML file (open source parser)
   */
  const parseMusicXMLFile = async (file) => {
  setParseStatus('Parsing MusicXML...');
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const xmlText = e.target.result;
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

        const parserError = xmlDoc.querySelector('parsererror');
        if (parserError) {
          throw new Error('XML parsing failed');
        }

        const musicData = {
          format: 'musicxml',
          title: xmlDoc.querySelector('work-title')?.textContent || 
                 xmlDoc.querySelector('movement-title')?.textContent || 
                 file.name.replace(/\.(xml|mxl|musicxml)$/i, ''),
          composer: xmlDoc.querySelector('creator[type="composer"]')?.textContent || 'Unknown',
          parts: []
        };

        const scoreParts = xmlDoc.querySelectorAll('score-part');
        
        scoreParts.forEach((scorePart, partIndex) => {
          const partId = scorePart.getAttribute('id');
          const partName = scorePart.querySelector('part-name')?.textContent || `Part ${partIndex + 1}`;
          
          const partElement = xmlDoc.querySelector(`part[id="${partId}"]`);
          
          if (partElement) {
            musicData.parts.push({
              id: partId,
              name: partName,
              measures: extractMeasuresSimple(partElement),
              partIndex: partIndex
            });
          }
        });

        // Simple measure extraction - take the highest note in each measure
        function extractMeasuresSimple(partElement) {
          const measures = partElement.querySelectorAll('measure');
          const measureData = [];
          let partClef = null;
          
          measures.forEach(measure => {
            const data = {
              number: measure.getAttribute('number'),
              notes: []
            };

            const attributes = measure.querySelector('attributes');
            if (attributes) {
              const fifths = attributes.querySelector('key fifths')?.textContent;
              const timeBeats = attributes.querySelector('time beats')?.textContent;
              const timeBeatType = attributes.querySelector('time beat-type')?.textContent;
              
              const clef = attributes.querySelector('clef');
              if (clef) {
                const sign = clef.querySelector('sign')?.textContent;
                const line = clef.querySelector('line')?.textContent;
                partClef = `${sign}${line}`;
                data.clef = partClef;
              }
              
              if (fifths) data.key = fifths;
              if (timeBeats && timeBeatType) {
                data.timeSignature = `${timeBeats}/${timeBeatType}`;
              }
            }

            // Get all notes - take only the primary melody line (voice 1, highest pitch)
            const notes = measure.querySelectorAll('note');
            notes.forEach(note => {
              // Skip chord tones (they have <chord/> element)
              if (note.querySelector('chord')) return;
              
              const isRest = note.querySelector('rest') !== null;
              const noteData = { isRest };

              if (!isRest) {
                const pitch = note.querySelector('pitch');
                if (pitch) {
                  noteData.step = pitch.querySelector('step')?.textContent;
                  noteData.octave = pitch.querySelector('octave')?.textContent;
                  noteData.alter = pitch.querySelector('alter')?.textContent;
                }
              }

              noteData.duration = note.querySelector('duration')?.textContent;
              noteData.type = note.querySelector('type')?.textContent;

              data.notes.push(noteData);
            });

            measureData.push(data);
          });
          
          return measureData;
        }

        if (musicData.parts.length === 0) {
          throw new Error('No musical parts found');
        }

        resolve(musicData);
      } catch (error) {
        reject(new Error('Failed to parse MusicXML: ' + error.message));
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};
  /**
   * Parse PDF file (using pdf.js - open source)
   */
  const parsePDFFile = async (file) => {
    setParseStatus('Analyzing PDF... (This feature requires PDF.js library)');
    
    // Note: Full implementation requires pdf.js
    // For MVP, we'll extract text and guide user to use MusicXML
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          // Basic PDF parsing would go here
          // For production, integrate pdf.js: https://mozilla.github.io/pdf.js/
          
          setParseStatus('PDF parsing is limited. Please use MusicXML format for best results.');
          
          resolve({
            format: 'pdf',
            title: file.name.replace('.pdf', ''),
            message: 'PDF import is under development. Please export your PDF to MusicXML format using MuseScore (free) or other notation software, then upload the .xml file.',
            recommendation: 'Use MuseScore (free, open-source) to convert PDF → MusicXML'
          });
        } catch (error) {
          reject(new Error('PDF parsing failed: ' + error.message));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read PDF'));
      reader.readAsArrayBuffer(file);
    });
  };

  /**
   * Parse image file (for OMR - Optical Music Recognition)
   */
  const parseImageFile = async (file) => {
    setParseStatus('Scanning image... (OMR feature in development)');
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          // For production OMR, consider Audiveris (open source) or similar
          setParseStatus('Image recognition is limited. Please use MusicXML for best results.');
          
          resolve({
            format: 'image',
            title: file.name,
            imageData: e.target.result,
            message: 'Optical Music Recognition (OMR) is under development. Please use MusicXML format or convert your sheet music using free software like MuseScore.',
            recommendation: 'Use MuseScore (free) to create digital scores, then export as MusicXML'
          });
        } catch (error) {
          reject(new Error('Image parsing failed: ' + error.message));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read image'));
      reader.readAsDataURL(file);
    });
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setUploading(true);
    setParseStatus('Reading file...');

    try {
      // Determine file type
      const extension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0];
      let format = null;

      for (const [key, config] of Object.entries(supportedFormats)) {
        if (config.extensions.includes(extension) || config.mimeTypes.includes(file.type)) {
          format = key;
          setFileType(config.label);
          break;
        }
      }

      if (!format) {
        throw new Error('Unsupported file format. Please upload MusicXML (.xml, .mxl), PDF, or image files.');
      }

      // Parse file based on type
      let musicData;
      switch (format) {
        case 'musicxml':
          musicData = await parseMusicXMLFile(file);
          break;
        case 'pdf':
          musicData = await parsePDFFile(file);
          break;
        case 'image':
          musicData = await parseImageFile(file);
          break;
        default:
          throw new Error('Parser not implemented for this format');
      }

      setParseStatus('✓ File parsed successfully!');
      onMusicDataParsed(musicData);

    } catch (error) {
      setParseStatus('✗ Error: ' + error.message);
      onError(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Import Western Music Sheet</h3>
      
      {/* Supported Formats */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-3">
        {Object.entries(supportedFormats).map(([key, config]) => {
          const IconComponent = config.icon;
          return (
            <div key={key} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <IconComponent className="w-5 h-5 text-purple-600" />
              <div>
                <div className="text-sm font-semibold text-gray-800">{config.label}</div>
                <div className="text-xs text-gray-500">
                  {config.extensions.join(', ')}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Upload Area */}
      <div
        onClick={() => fileInputRef.current?.click()}
        className="border-2 border-dashed border-purple-300 rounded-lg p-8 text-center cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-all"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xml,.mxl,.musicxml,.pdf,.png,.jpg,.jpeg"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader className="w-12 h-12 text-purple-600 animate-spin" />
            <p className="text-gray-700 font-medium">{parseStatus}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <Upload className="w-12 h-12 text-purple-600" />
            <p className="text-gray-700 font-medium">
              Click to upload or drag & drop
            </p>
            <p className="text-sm text-gray-500">
              MusicXML, PDF, or Image files
            </p>
          </div>
        )}
      </div>

      {/* Status Message */}
      {parseStatus && !uploading && (
        <div className={`mt-4 p-4 rounded-lg flex items-start gap-3 ${
          parseStatus.startsWith('✓') ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
        }`}>
          {parseStatus.startsWith('✓') ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-800">{parseStatus}</p>
            {fileName && (
              <p className="text-xs text-gray-600 mt-1">File: {fileName}</p>
            )}
          </div>
        </div>
      )}

      {/* Recommendation Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-2">
              📝 Best Practice for Importing
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li><strong>✓ Recommended:</strong> MusicXML format (.xml, .mxl) - preserves all musical data</li>
              <li><strong>Free Tool:</strong> Use <a href="https://musescore.org" target="_blank" rel="noopener noreferrer" className="underline font-semibold">MuseScore</a> (open-source) to create or convert sheets</li>
              <li><strong>From PDF:</strong> Open PDF in MuseScore → Export as MusicXML</li>
              <li><strong>From Scanned Images:</strong> Use Audiveris (open-source OMR) or MuseScore's import feature</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileImport;